import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, X, ArrowRight, ChevronDown, GraduationCap, CalendarCheck, Sprout, Users, HeartHandshake, ShieldCheck, NotebookPen } from "lucide-react";
import { IMG, METHODS, ART_CARDS, ASSETS } from "@/data/content";
import { lockScroll, unlockScroll } from "@/lib/scroll";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { FleurDivider } from "@/components/Ornament";
import { imgProps } from "@/lib/img";

const ABOUT_POINTS = [
  { icon: Users, text: "Мала кількість дітей у групах — увага кожному" },
  { icon: HeartHandshake, text: "М’яка адаптація у комфортному темпі дитини" },
  { icon: ShieldCheck, text: "Емоційна підтримка та безпечне середовище" },
  { icon: NotebookPen, text: "Щоденний звіт для батьків про день малюка" },
];

function AboutModal({ onClose }) {
  return (
    <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-3" data-testid="about-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-[#030a07]/85 backdrop-blur-md"
        onClick={onClose}
        data-testid="about-modal-backdrop"
      />
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="relative z-10 w-[95%] max-w-xl max-h-[85vh] overflow-y-auto overscroll-contain magical-scrollbar rounded-3xl glass p-6 sm:p-9 shadow-[0_0_80px_rgba(230,184,92,0.2)]"
        data-lenis-prevent
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center text-amber-100 hover:text-amber-300 hover:rotate-90 transition-all duration-300 cursor-pointer"
          data-testid="about-modal-close"
          aria-label="Закрити"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white pr-12" data-testid="about-modal-title">
          «Любов і турбота — наш фундамент»
        </h3>
        <p className="mt-5 text-slate-200/85 text-sm sm:text-base leading-relaxed">
          Наша філософія проста: дитина розкривається лише там, де її люблять. Тому кожен день у наших
          садочках побудований на повазі до особистості, м’якій адаптації та теплому контакті з педагогами.
        </p>
        <div className="mt-6 space-y-3">
          {ABOUT_POINTS.map((p, i) => (
            <div
              key={p.text}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5"
              data-testid={`about-modal-point-${i}`}
            >
              <span className="w-9 h-9 rounded-xl bg-amber-300/15 border border-amber-300/30 flex items-center justify-center shrink-0">
                <p.icon className="w-4 h-4 text-amber-300" />
              </span>
              <span className="text-sm text-slate-100/95">{p.text}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export function About() {
  const [open, setOpen] = useState(false);
  const openModal = () => { setOpen(true); lockScroll(); };
  const closeModal = () => { setOpen(false); unlockScroll(); };
  const aboutImg = imgProps(IMG.aboutKids, "(max-width: 1152px) 100vw, 1152px");

  return (
    <section id="pro-nas" className="relative py-12 px-3 sm:py-20 sm:px-4 overflow-hidden" data-testid="about-section">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMG.gardenBg})` }}
        data-testid="about-bg"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, #061812 0%, rgba(6,24,18,0.82) 10%, rgba(6,24,18,0.45) 30%, rgba(6,24,18,0.32) 55%, rgba(6,24,18,0.55) 78%, rgba(6,24,18,0.88) 92%, #061812 100%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto">
        <Reveal className="text-center">
          <Eyebrow number="03" text="Про нас" />
          <h2
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-4xl mx-auto"
            data-testid="about-title"
          >
            ПРОСТІР, ЯКИЙ СТАЄ <span className="text-gold-gradient">ПРОДОВЖЕННЯМ ДОМУ</span>
          </h2>
        </Reveal>

        <Reveal delay={0.15} className="mt-8 sm:mt-12">
          <div
            className="relative rounded-[2rem] overflow-hidden border border-amber-400/20 shadow-[0_25px_70px_rgba(0,0,0,0.5)] hidden md:block"
            data-testid="about-photo-frame"
          >
            <img
              {...aboutImg}
              alt="Щасливі діти садочка"
              className="w-full h-auto block"
              style={{ ...aboutImg.style, marginTop: "-12%" }}
              data-testid="about-photo"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#061812]/85 via-[#061812]/10 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 p-5 sm:p-9 max-w-xl">
              <div className="font-cinzel text-amber-300 text-xs sm:text-sm tracking-[0.3em] uppercase" data-testid="about-overlay-badge">
                Любов і турбота
              </div>
              <p className="mt-2.5 text-white/95 text-sm sm:text-lg leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]" data-testid="about-overlay-text">
                Ми створюємо простір, у якому дитина почувається як у дома — любові, турботі та безпеці.
              </p>
            </div>
          </div>

          <div
            className="relative rounded-3xl overflow-hidden border border-amber-400/20 shadow-[0_25px_70px_rgba(0,0,0,0.5)] md:hidden aspect-[5/4]"
            data-testid="about-photo-frame-mobile"
          >
            <img
              {...imgProps(IMG.aboutKids, "100vw")}
              alt="Щасливі діти садочка"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "50% 82%" }}
              data-testid="about-photo-mobile"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#061812]/90 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="font-cinzel text-amber-300 text-xs tracking-[0.3em] uppercase" data-testid="about-overlay-badge-mobile">
                Любов і турбота
              </div>
              <p className="mt-2 text-white/95 text-sm leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]" data-testid="about-overlay-text-mobile">
                Ми створюємо простір, у якому дитина почувається як у дома — любові, турботі та безпеці.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25} className="mt-6 sm:mt-9 text-center">
          <button
            type="button"
            onClick={openModal}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold px-8 py-3.5 text-sm sm:text-base shadow-[0_0_28px_rgba(230,184,92,0.45)] hover:shadow-[0_0_42px_rgba(230,184,92,0.75)] transition-shadow duration-300 cursor-pointer"
            data-testid="about-details-button"
          >
            Детальніше
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>

      <AnimatePresence>{open && <AboutModal onClose={closeModal} />}</AnimatePresence>
    </section>
  );
}

const ENGLISH_STEPS = [
  {
    icon: GraduationCap,
    title: "Заняття з професійними педагогами",
    text: "«Щоденні уроки англійської у цікавій та ігровій формі.»",
  },
  {
    icon: CalendarCheck,
    title: "Англійська — регулярно",
    text: "«Щоденні заняття допомагають поступово формувати словниковий запас, розуміння мови та впевненість у спілкуванні.»",
  },
  {
    icon: Sprout,
    title: "Природний розвиток мови",
    text: "«Дитина засвоює мову так само, як рідну — без зубріння та примусу.»",
  },
];

export function EnglishDaily() {
  const [showSteps, setShowSteps] = useState(false);

  return (
    <section className="relative py-14 sm:py-20 px-4" data-testid="english-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
        <Reveal className="order-2 lg:order-1 lg:sticky lg:top-28">
          <div className="rounded-[2rem] overflow-hidden border border-blue-300/20 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <img
              {...imgProps(IMG.english, "(max-width: 1024px) 92vw, 560px")}
              alt="Англійська щодня"
              className="w-full h-72 sm:h-96 object-cover hover:scale-105 transition-transform duration-700"
              data-testid="english-photo"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2">
          <Eyebrow number="04" text="Мова" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-testid="english-title">
            АНГЛІЙСЬКА <span className="text-gold-gradient">ЩОДНЯ</span>
          </h2>
          <p className="mt-6 text-slate-200/85 text-sm sm:text-base leading-relaxed max-w-lg">
            Діти щодня занурюються в англійську мову та природно засвоюють її у процесі спілкування,
            гри й навчання.
          </p>

          <button
            type="button"
            onClick={() => setShowSteps((v) => !v)}
            aria-expanded={showSteps}
            className="group mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold px-7 py-3.5 text-sm sm:text-base shadow-[0_0_28px_rgba(230,184,92,0.45)] hover:shadow-[0_0_42px_rgba(230,184,92,0.75)] transition-shadow duration-300 cursor-pointer"
            data-testid="english-how-button"
          >
            Як це працює
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-400 ${showSteps ? "rotate-180" : "group-hover:translate-y-0.5"}`}
            />
          </button>

          <AnimatePresence initial={false}>
            {showSteps && (
              <motion.div
                key="steps"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                data-testid="english-steps"
              >
                <div className="pt-6 space-y-3.5 max-w-lg">
                  {ENGLISH_STEPS.map((s, i) => (
                    <motion.div
                      key={s.title}
                      initial={{ opacity: 0, y: -22 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -14 }}
                      transition={{ delay: 0.15 + i * 0.16, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      className="flex items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-4 hover:bg-white/[0.07] transition-colors duration-300"
                      data-testid={`english-step-${i}`}
                    >
                      <span className="shrink-0 w-10 h-10 rounded-xl bg-amber-300/15 border border-amber-300/30 flex items-center justify-center">
                        <s.icon className="w-5 h-5 text-amber-300" />
                      </span>
                      <div>
                        <div className="font-semibold text-white text-sm sm:text-base leading-snug">
                          {i + 1}. {s.title}
                        </div>
                        <p className="mt-1.5 text-sm text-slate-300/85 leading-relaxed">{s.text}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
}

const METHOD_DETAILS = [
  { name: "Монтессорі", text: "самостійність, сенсорний розвиток, вільний вибір діяльності" },
  { name: "Нікітіна", text: "логічні ігри та інтелектуальні сходинки" },
  { name: "Домана", text: "ранній інтелектуальний розвиток" },
  { name: "Зайцева", text: "легке читання через кубики та гру" },
  { name: "Авторський підхід", text: "вікові модулі та індивідуальний темп кожної дитини" },
];

function MethodologiesModal({ onClose }) {
  return (
    <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-3" data-testid="methods-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-[#030a07]/85 backdrop-blur-md"
        onClick={onClose}
        data-testid="methods-modal-backdrop"
      />
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 50 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="relative z-10 w-[95%] max-w-xl max-h-[85vh] overflow-y-auto magical-scrollbar rounded-3xl glass p-6 sm:p-9 shadow-[0_0_80px_rgba(230,184,92,0.2)]"
        data-lenis-prevent
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center text-amber-100 hover:text-amber-300 hover:rotate-90 transition-all duration-300 cursor-pointer"
          data-testid="methods-modal-close"
          aria-label="Закрити"
        >
          <X className="w-5 h-5" />
        </button>
        <img
          src={ASSETS.giraffe}
          alt="Помічник — жираф"
          className="absolute top-4 left-4 sm:top-5 sm:left-5 w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover bg-white ring-2 ring-amber-300/60 shadow-[0_6px_20px_rgba(0,0,0,0.4)]"
          loading="lazy"
          decoding="async"
          data-testid="methods-modal-avatar"
        />
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-white pl-16 sm:pl-20" data-testid="methods-modal-title">
          «Як ми навчаємо»
        </h3>
        <FleurDivider id="ornMethods" size="w-4 h-4" className="mt-4 opacity-80" />
        <div className="mt-6 space-y-3">
          {METHOD_DETAILS.map((m, i) => (
            <div key={m.name} className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5" data-testid={`methods-modal-item-${i}`}>
              <span className="font-cinzel text-amber-200 text-sm tracking-wider">{m.name}</span>
              <span className="text-slate-100/90 text-sm">: {m.text}</span>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center font-display italic text-lg sm:text-xl text-amber-100/90 leading-snug" data-testid="methods-modal-focus">
          «Головний фокус — розвиток творчого потенціалу кожної дитини.»
        </p>
      </motion.div>
    </motion.div>
  );
}

export function Methodologies() {
  const [open, setOpen] = useState(false);
  const openModal = () => { setOpen(true); lockScroll(); };
  const closeModal = () => { setOpen(false); unlockScroll(); };

  return (
    <section className="relative py-12 sm:py-16 px-4" data-testid="methods-section">
      <Reveal>
        <Eyebrow number="06" text="Методики" />
        <h2 className="font-display text-center text-2xl sm:text-3xl lg:text-4xl font-bold text-white max-w-3xl mx-auto leading-snug" data-testid="methods-title">
          НАЙКРАЩІ МЕТОДИКИ — <span className="text-gold-gradient">ДЛЯ ГАРМОНІЙНОГО РОЗВИТКУ</span>
        </h2>
        <p className="mt-5 text-center text-slate-200/85 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto" data-testid="methods-description">
          Ми поєднуємо адаптовані елементи світових методик із авторським підходом, де матеріали поділені на вікові модулі.
        </p>
        <div className="relative mt-10 max-w-3xl mx-auto">
          <img
            src={ASSETS.giraffe}
            alt="Помічник — жираф"
            className="absolute -top-7 -left-2 sm:-left-8 z-10 w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover bg-white ring-2 ring-amber-300/60 shadow-[0_10px_30px_rgba(0,0,0,0.5)] animate-float-soft"
            loading="lazy"
            decoding="async"
            data-testid="methods-giraffe"
          />
          <div className="glass rounded-[2rem] px-5 sm:px-8 py-8 sm:py-9 flex flex-wrap items-center justify-center gap-3 sm:gap-4" data-testid="methods-list">
            {METHODS.map((m, i) => (
              <span
                key={m}
                className="rounded-full border border-amber-400/20 bg-white/[0.04] px-6 py-3 font-cinzel text-sm sm:text-base text-amber-100 hover:text-amber-300 hover:border-amber-300/50 hover:-translate-y-1 transition-all duration-300 cursor-default"
                data-testid={`method-${i}`}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
        <div className="mt-9 text-center">
          <button
            type="button"
            onClick={openModal}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-semibold px-8 py-3.5 text-sm sm:text-base shadow-[0_0_28px_rgba(230,184,92,0.45)] hover:shadow-[0_0_42px_rgba(230,184,92,0.75)] transition-shadow duration-300 cursor-pointer"
            data-testid="methods-details-button"
          >
            Деталі у жирафа
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </Reveal>
      <AnimatePresence>{open && <MethodologiesModal onClose={closeModal} />}</AnimatePresence>
    </section>
  );
}

export function ArtTherapy() {
  return (
    <section className="relative py-12 sm:py-16 px-4" data-testid="art-therapy-section">
      <Reveal className="max-w-3xl mx-auto text-center">
        <Eyebrow number="07" text="Арт-терапія" />
        <Quote className="w-10 h-10 text-amber-300/60 mx-auto mb-6" />
        <blockquote
          className="font-display italic text-2xl sm:text-3xl lg:text-4xl text-amber-50 leading-snug"
          data-testid="art-therapy-quote"
        >
          «Щоб дитина могла не лише говорити про свої почуття, а й виражати їх.»
        </blockquote>
        <div className="mt-8 h-px w-32 mx-auto bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      </Reveal>

      <div className="max-w-6xl mx-auto mt-12 sm:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 items-start" data-testid="art-therapy-widgets">
        {ART_CARDS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div
              className="group rounded-3xl overflow-hidden border border-amber-400/15 bg-[#0a211a] hover:border-amber-300/45 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(230,184,92,0.15)] transition-all duration-500 h-full"
              data-testid={`art-card-${i}`}
            >
              <div className="overflow-hidden">
                <img
                  {...imgProps(c.img, "(max-width: 640px) 45vw, 25vw")}
                  alt={c.title}
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="p-4 sm:p-5">
                <span className="font-semibold text-white text-sm sm:text-base leading-snug block text-center">
                  {c.title}
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
